import React, { useState } from 'react';
import { Button, Divider, Popconfirm, Space, Tooltip } from 'antd';
import './styles.less';
import { CloseOutlined, DeleteOutlined, EyeInvisibleOutlined, EyeOutlined, SaveOutlined, UndoOutlined } from '@ant-design/icons';

/**
 * Sidebar with Publish, Save, and Cancel actions
 * @param {Form} form form item 
 * @param {object} item data item 
 * @param {string} saveTooltip message to be displayed on save button 
 * @param {function} onSaveCb callback function on save click 
 * @param {function} onCancelCb callback function on cancel click 
 * @param {function} onPublishCb callback function on publish click 
 */
const Sidebar = ( { form, item, tabFields, tab, disabled,
    onSaveCb, canSave: _canSave, saveTooltip, 
    onCancelCb, cancelTooltip, 
    onResetCb, resetTooltip,
    onPublishCb, isPublished, canPublish: _canPublish, publishTooltip,
    onDeleteCb, canDelete: _canDelete, } ) => {
    
    const [ publishing, setPublishing ] = useState( false );
    const [ saving, setSaving ] = useState( false );
    const [ canceling, setCanceling ] = useState( false );
    const [ deleting, setDeleting ] = useState( false );
    const [ reseting, setReseting ] = useState( false );

    const doingSomething = publishing || canceling || reseting || deleting;

    const canSave = !doingSomething && _canSave && !disabled;
    const canCancel = !doingSomething;
    const canDelete = !doingSomething && !!item && _canDelete && !disabled;
    const canReset = !doingSomething && onResetCb && tab && tabFields && !disabled;
    const canPublish = !doingSomething && !!item && _canPublish && !disabled;

    async function onSave() {
        if ( !canSave ) {return;}
        setSaving( true );
        await onSaveCb();
        setSaving( false );
    }

    async function onCancel() {
        if ( !canCancel ) {return;}
        setCanceling( true );
        await onCancelCb();
        setCanceling( false );
    }

    async function onDelete() {
        if ( !canDelete ) {return;}
        setDeleting( true );
        await onDeleteCb?.();
        setDeleting( false );
    }

    // TODO: this should be enhanced so it disables if there are no changes
    async function onReset() {
        if ( !canReset ) {return;}
        setReseting( true );
        await onResetCb( tab, tabFields );
        setReseting( false );
    }

    async function onPublish() {
        if ( !canPublish ) {return;}
        setPublishing( true );
        if ( item.isPublished ) {
            await onPublishCb( false );
        } else {
            await onPublishCb( true );
        }
        setPublishing( false );
    }

    return <Space direction='vertical' align='start' className='sidebar'>
        <Tooltip title={ saveTooltip ?? undefined } mouseEnterDelay={ 0.5 }>
            <Button type='primary' loading={ saving } disabled={ !canSave } onClick={ onSave } icon={ <SaveOutlined /> }>Save</Button>
        </Tooltip>
        <Tooltip title={ publishTooltip ?? undefined } mouseEnterDelay={ 0.5 }>
            <Button disabled={ !canPublish } loading={ publishing } variant='outlined' color={ isPublished ? 'volcano' : 'green' } onClick={ onPublish } icon={ isPublished ? <EyeInvisibleOutlined /> : <EyeOutlined /> }>{ isPublished ? 'Unpublish & Save' : 'Publish & Save' }</Button>
        </Tooltip>
        <Tooltip title={ cancelTooltip ?? undefined } mouseEnterDelay={ 0.5 }>
            <Button disabled={ !canCancel } loading={ canceling } onClick={ onCancel } icon={ <CloseOutlined /> }>Cancel</Button>
        </Tooltip>
        { canReset && <Popconfirm 
            title='Discard all changes to this tab?'
            onConfirm={ onReset } okText='Yes' cancelText='No'
            >
            <Button disabled={ !canReset } loading={ reseting } icon={ <UndoOutlined spin={ reseting } /> }>Discard Changes</Button>
        </Popconfirm> }
        
        <Divider />

        <Popconfirm 
            title='Are you sure you want to delete?'
            onConfirm={ onDelete } okText='Yes' cancelText='No'
            >
            <Button className='delete-button' loading={ deleting } disabled={ !canDelete } icon={ <DeleteOutlined /> } color='red' variant='outlined' >Delete</Button>
        </Popconfirm>
    </Space>;
};

export default Sidebar;